// Product editor slice. Holds the *draft* of the product currently being edited
// in the catalog board's product dialog, kept separate from the server-data
// `adminCatalog` slice so there's a single source of truth for in-progress edits
// (no copying server rows into component-local state). DEFAULT_DRAFT lives in
// initialState, so opening a "new product" seeds from defaults and closing the
// dialog reverts the draft back to those defaults.
import { createSlice } from '@reduxjs/toolkit';
import { adminFetch, AdminAuthError } from '../api/admin-client.js';
import { clearAdminSession } from './admin-auth-slice.js';
import {
  saveCatalogItem, savePricing, applyReorder, reorderCatalog, clearSaveError,
  saveProductIncludes,
} from './admin-catalog-slice.js';
import {
  findProductTiers, findProductRule, optionsForProduct,
} from '../utils/catalog-helpers.js';
import { includeDraftEntries } from '../utils/product-includes.js';
import { positionInCategory, placementPayload } from '../utils/product-placement.js';
import { normalizeLongDescription } from '../utils/rtf-helpers.js';
import { tiersFromRule, emptyTiers } from '../config/pricing-buckets.js';
import {
  DEFAULT_STRATEGY, strategyFromRule, amountFromRule,
} from '../config/pricing-strategies.js';

const DEFAULT_DRAFT = {
  name: '',
  short_description: '',
  long_description: '',
  icon_key: '',
  category_id: '',
  selection_mode: 'single',
  display_order: 1,
  is_active: true,
};

const initialState = {
  open: false,
  productId: null,
  isNew: false,
  draft: DEFAULT_DRAFT,
  tiers: emptyTiers(),
  strategy: DEFAULT_STRATEGY,
  flatAmount: 0,
  hasOptions: false,
  // One-way "this product auto-includes these others" draft, as editor entries:
  // [{ included_product_id, option_ids: number[] }]. Persisted on save.
  includes: [],
  images: [],
  // Images staged for a not-yet-saved product. Each: { tempId, file,
  // previewUrl, alt_text }. Uploaded to the server on save once the product
  // row (and its id) exists; an existing product uploads immediately instead.
  pendingImages: [],
  imagesBusy: false,
  imagesError: null,
};

const productEditorSlice = createSlice({
  name: 'productEditor',
  initialState,
  reducers: {
    // Seed the editor from a fully-resolved payload (built by openProductEditor).
    openEditor: (state, action) => {
      const {
        productId, isNew, draft, tiers, strategy, flatAmount, hasOptions, includes,
      } = action.payload;
      state.open = true;
      state.productId = productId;
      state.isNew = isNew;
      state.draft = draft;
      state.tiers = tiers;
      state.strategy = strategy;
      state.flatAmount = flatAmount;
      state.hasOptions = hasOptions;
      state.includes = includes;
      state.images = [];
      state.pendingImages = [];
      state.imagesBusy = false;
      state.imagesError = null;
    },
    // Revert everything to defaults so the next open starts clean.
    closeEditor: () => initialState,
    setImages: (state, action) => { state.images = action.payload; },
    setImagesBusy: (state, action) => { state.imagesBusy = action.payload; },
    setImagesError: (state, action) => { state.imagesError = action.payload; },
    pushPendingImage: (state, action) => { state.pendingImages.push(action.payload); },
    dropPendingImage: (state, action) => {
      state.pendingImages = state.pendingImages.filter((p) => p.tempId !== action.payload);
    },
    reorderPendingImages: (state, action) => {
      const order = action.payload;
      const byId = new Map(state.pendingImages.map((p) => [p.tempId, p]));
      state.pendingImages = order.map((id) => byId.get(id)).filter(Boolean);
    },
    clearPendingImages: (state) => { state.pendingImages = []; },
    setDraftField: (state, action) => {
      const { field, value } = action.payload;
      state.draft[field] = value;
    },
    setDraftName: (state, action) => {
      state.draft.name = action.payload;
    },
    setCategoryPlacement: (state, action) => {
      const { categoryId, position } = action.payload;
      state.draft.category_id = categoryId;
      state.draft.display_order = position;
    },
    setTiers: (state, action) => { state.tiers = action.payload; },
    setStrategy: (state, action) => { state.strategy = action.payload; },
    setFlatAmount: (state, action) => { state.flatAmount = action.payload; },
    setHasOptions: (state, action) => { state.hasOptions = action.payload; },
    setIncludes: (state, action) => { state.includes = action.payload; },
    setProductId: (state, action) => {
      state.productId = action.payload;
    },
  },
});

export const {
  openEditor, closeEditor, setDraftField, setDraftName,
  setCategoryPlacement, setTiers, setStrategy, setFlatAmount, setHasOptions, setProductId,
  setImages, setImagesBusy, setImagesError, setIncludes,
  pushPendingImage, dropPendingImage, reorderPendingImages, clearPendingImages,
} = productEditorSlice.actions;

// Open the editor for an existing product, or a new one in `defaultCategoryId`.
// Resolves the draft (including the product's 1-based position in its column),
// its pricing tiers, and whether it has options — all from the catalog slice —
// so the dialog itself never derives from server data.
export const openProductEditor = ({ product, defaultCategoryId } = {}) => (dispatch, getState) => {
  const {
    options, pricing, products, includes,
  } = getState().adminCatalog.data;
  const categoryId = product?.category_id ?? defaultCategoryId ?? '';
  const position = positionInCategory(products, categoryId, product?.id);
  const rule = product ? findProductRule(pricing, product.id) : null;
  dispatch(clearSaveError());
  dispatch(openEditor({
    productId: product?.id ?? null,
    isNew: !product,
    draft: {
      ...DEFAULT_DRAFT,
      ...(product || {}),
      category_id: categoryId,
      display_order: position,
    },
    tiers: tiersFromRule(product ? findProductTiers(pricing, product.id) : null),
    strategy: strategyFromRule(rule),
    flatAmount: amountFromRule(rule),
    hasOptions: product ? optionsForProduct(options, product.id).length > 0 : false,
    includes: product ? includeDraftEntries(includes, product.id) : [],
  }));
};

// Move the draft to another category, dropping it at the end of that column.
export const changeDraftCategory = (categoryId) => (dispatch, getState) => {
  const { products } = getState().adminCatalog.data;
  const { productId } = getState().productEditor;
  const count = products
    .filter((p) => p.category_id === Number(categoryId) && p.id !== productId).length;
  dispatch(setCategoryPlacement({ categoryId, position: count + 1 }));
};

// The product image API is keyed by the saved product id.
const imageBase = (productId) => `/api/admin/products/${productId}/images`;

// Upload every staged image (in order) once the product row exists, then swap
// the local previews for the server's image list. Sequential so display_order
// matches the staged order. Returns { ok } so save can react to a failure.
export const commitPendingImages = (productId) => async (dispatch, getState) => {
  const { pendingImages } = getState().productEditor;
  if (pendingImages.length === 0) return { ok: true };

  dispatch(setImagesBusy(true));
  dispatch(setImagesError(null));
  try {
    await pendingImages.reduce(async (prev, pending) => {
      await prev;
      const form = new FormData();
      form.append('image', pending.file);
      if (pending.alt_text) form.append('alt_text', pending.alt_text);
      await adminFetch(imageBase(productId), { method: 'POST', body: form });
    }, Promise.resolve());

    pendingImages.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    dispatch(clearPendingImages());
    const { items } = await adminFetch(imageBase(productId));
    dispatch(setImages(items));
    return { ok: true };
  } catch (err) {
    if (err instanceof AdminAuthError) dispatch(clearAdminSession());
    dispatch(setImagesError(err.message));
    return { ok: false };
  } finally {
    dispatch(setImagesBusy(false));
  }
};

// Save the draft: upsert the product row, persist its column placement, and (for
// a simple product) upsert its pricing. Returns `{ ok, keepOpen }` — a brand-new
// option-bearing product stays open so its options can be added (options are
// keyed by the product id, which only exists after this save).
export const saveProductDraft = () => async (dispatch, getState) => {
  const {
    draft, tiers, strategy, flatAmount, hasOptions, productId, includes,
  } = getState().productEditor;
  // Capture before saving: a row that doesn't exist yet is a create.
  const isCreate = !productId;

  const saved = await dispatch(saveCatalogItem({
    resource: 'products',
    id: productId,
    values: {
      name: draft.name.trim(),
      category_id: draft.category_id,
      short_description: draft.short_description?.trim() || null,
      long_description: normalizeLongDescription(draft.long_description),
      icon_key: draft.icon_key?.trim() || null,
      selection_mode: hasOptions ? draft.selection_mode : 'single',
      is_active: draft.is_active,
    },
  }));
  if (!saveCatalogItem.fulfilled.match(saved)) return { ok: false };

  const { item } = saved.payload;
  dispatch(setProductId(item.id));

  const { products } = getState().adminCatalog.data;
  const payload = placementPayload(products, item, draft.category_id, draft.display_order);
  dispatch(applyReorder(payload));
  dispatch(reorderCatalog(payload));

  // Upload any locally-staged images now that the product row (and id) exists.
  const imagesResult = await dispatch(commitPendingImages(item.id));

  // Persist the one-way "included products" set now that the parent id exists.
  const includesSaved = await dispatch(saveProductIncludes({
    productId: item.id,
    includes,
  }));
  const includesOk = saveProductIncludes.fulfilled.match(includesSaved);

  if (!hasOptions) {
    const priced = await dispatch(savePricing({
      target: { product_id: item.id },
      strategy,
      tiers,
      amount: Number(flatAmount) || 0,
    }));
    const ok = savePricing.fulfilled.match(priced) && imagesResult.ok && includesOk;
    // Keep the dialog open if an image upload failed so the error stays visible
    // and the user can retry (productId is now set → server-mode images).
    return { ok, keepOpen: !imagesResult.ok || !includesOk };
  }
  // Option-bearing: keep a brand-new product open to add its options; close an
  // edit of an existing one. Also keep open if an image/includes write failed.
  return {
    ok: imagesResult.ok && includesOk,
    keepOpen: isCreate || !imagesResult.ok || !includesOk,
  };
};

// Close the editor, revoking any staged-image preview URLs first so we don't
// leak object URLs when a new product is abandoned without saving.
export const discardEditor = () => (dispatch, getState) => {
  getState().productEditor.pendingImages.forEach((p) => URL.revokeObjectURL(p.previewUrl));
  dispatch(closeEditor());
};

// --- Product images -----------------------------------------------------------
// An existing product uploads/edits images immediately against its row. A
// brand-new product has no id yet, so its images are staged locally (as File
// objects with object-URL previews) and uploaded on save (commitPendingImages,
// defined above). All share imagesBusy/imagesError; a dead session bounces to
// login.

// Client-side guards mirroring the server (middleware/upload-image.js) so a
// staged image gets immediate feedback instead of failing only at save time.
const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const IMAGE_MAX_PER_PRODUCT = 8;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

let pendingImageSeq = 0;

const withImageRequest = (fn) => async (dispatch, getState) => {
  dispatch(setImagesBusy(true));
  dispatch(setImagesError(null));
  try {
    await fn(dispatch, getState);
  } catch (err) {
    if (err instanceof AdminAuthError) dispatch(clearAdminSession());
    dispatch(setImagesError(err.message));
  } finally {
    dispatch(setImagesBusy(false));
  }
};

// Remove a staged image, revoking its preview URL.
export const removePendingImage = (tempId) => (dispatch, getState) => {
  const pending = getState().productEditor.pendingImages.find((p) => p.tempId === tempId);
  if (pending) URL.revokeObjectURL(pending.previewUrl);
  dispatch(dropPendingImage(tempId));
};

// Stage one or more files for a not-yet-saved product. Validates each and
// surfaces a single combined error if any were rejected (bad type/size) or
// skipped because the per-product limit was reached.
export const addPendingImages = (files) => (dispatch, getState) => {
  let skipped = false;
  let invalid = false;
  files.forEach((file) => {
    const { pendingImages } = getState().productEditor;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type) || file.size > IMAGE_MAX_BYTES) {
      invalid = true;
      return;
    }
    if (pendingImages.length >= IMAGE_MAX_PER_PRODUCT) {
      skipped = true;
      return;
    }
    pendingImageSeq += 1;
    dispatch(pushPendingImage({
      tempId: `pending-${pendingImageSeq}`,
      file,
      previewUrl: URL.createObjectURL(file),
      alt_text: '',
    }));
  });

  if (skipped) {
    dispatch(setImagesError(`A product can have at most ${IMAGE_MAX_PER_PRODUCT} images; some were not added.`));
  } else if (invalid) {
    dispatch(setImagesError('Some files were skipped. Use JPEG, PNG, or WebP up to 5 MB each.'));
  } else {
    dispatch(setImagesError(null));
  }
};

export const loadProductImages = (productId) => withImageRequest(async (dispatch) => {
  const { items } = await adminFetch(imageBase(productId));
  dispatch(setImages(items));
});

// Upload one or more files to an existing product, sequentially (so their
// display_order matches selection order), then refresh the list once. Files
// beyond the per-product limit are skipped with an explanatory error.
export const addProductImages = (productId, files) => withImageRequest(
  async (dispatch, getState) => {
    const current = getState().productEditor.images.length;
    const room = IMAGE_MAX_PER_PRODUCT - current;
    const toUpload = files.slice(0, Math.max(0, room));

    await toUpload.reduce(async (prev, file) => {
      await prev;
      const form = new FormData();
      form.append('image', file);
      await adminFetch(imageBase(productId), { method: 'POST', body: form });
    }, Promise.resolve());

    const { items } = await adminFetch(imageBase(productId));
    dispatch(setImages(items));

    if (files.length > toUpload.length) {
      dispatch(setImagesError(`A product can have at most ${IMAGE_MAX_PER_PRODUCT} images; some were not added.`));
    }
  },
);

export const removeProductImage = (productId, imageId) => withImageRequest(async (dispatch) => {
  await adminFetch(`${imageBase(productId)}/${imageId}`, { method: 'DELETE' });
  const { items } = await adminFetch(imageBase(productId));
  dispatch(setImages(items));
});

export const reorderProductImages = (productId, orderedIds) => withImageRequest(
  async (dispatch) => {
    const { items } = await adminFetch(`${imageBase(productId)}/order`, {
      method: 'PUT',
      body: { order: orderedIds },
    });
    dispatch(setImages(items));
  },
);

export default productEditorSlice.reducer;
